import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideFreeTrialModal from "@/features/pathpick/features/start-guide/components/finish/PathPickStartGuideFreeTrialModal";
import useStartGuideFreeTrial from "@/features/pathpick/features/start-guide/hooks/useStartGuideFreeTrial";

interface Props {
  onStartFreeTrial: (startedAtIso: string) => void;
  onPrev: () => void;
}

const SEARCH_PRODUCTS_PATH = "/pathpick/search-products";

/**
 * 안내 완료 — 문의 안내 + 완료 안내 + (최초 결제 대상만) 무료체험 프로모션.
 *
 * 대상이 아니면 프로모션 카드와 CTA를 모두 숨기고 `상품 찾기` 만 남긴다(전달서 §17).
 * 프로모션 카드에는 버튼을 두지 않는다 — 시작은 하단 CTA 하나로 모은다.
 */
function PathPickStartGuideFinishSection({ onStartFreeTrial, onPrev }: Props) {
  const { t } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  // 완료 페이지의 주 CTA 는 무료체험 대상 여부로 갈린다(전달서 §17).
  const { isEligible: isTrialEligible, start: startTrial } =
    useStartGuideFreeTrial(onStartFreeTrial);
  const goToSearchProducts = () => navigate(SEARCH_PRODUCTS_PATH);

  return (
    <>
      <Flex flexDirection={"column"} gap={"16px"}>
        <PathPickStartGuideCard
          role={"region"}
          aria-label={t("path_pick_guide:finish.contact_aria_label")}
          backgroundColor={"#F0F9FF"}
          borderColor={"#A8CEFA"}
        >
          <Flex gap={"12px"} alignItems={"flex-start"}>
            {/* 우측 하단 파란 원형 문의 버튼을 가리키는 아이콘 */}
            <Flex
              alignItems={"center"}
              justifyContent={"center"}
              flexShrink={0}
              width={"32px"}
              height={"32px"}
              borderRadius={"50%"}
              backgroundColor={"#1272EF"}
              color={"#FFFFFF"}
            >
              <CustomIcon src={"cs"} width={18} height={18} />
            </Flex>
            <Flex flexDirection={"column"} gap={"4px"}>
              <Text textStyle={"body-md-bold"} color={"#22221F"}>
                {t("path_pick_guide:finish.contact_title")}
              </Text>
              <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                {t("path_pick_guide:finish.contact_description")}
              </Text>
            </Flex>
          </Flex>
        </PathPickStartGuideCard>

        <PathPickStartGuideCard
          role={"region"}
          aria-label={t("path_pick_guide:finish.complete_aria_label")}
          backgroundColor={"#E9F7EF"}
          borderColor={"#A9E0AB"}
        >
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t("path_pick_guide:finish.title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:finish.description")}
            </Text>
          </Flex>
        </PathPickStartGuideCard>

        {/* 최초 결제 대상에게만 보여주는 프로모션. 시작 버튼은 아래 CTA 하나뿐이다. */}
        {isTrialEligible && (
          <PathPickStartGuideCard
            role={"region"}
            aria-label={t("path_pick_guide:finish.free_trial_aria_label")}
            backgroundColor={"#F0F9FF"}
            borderColor={"#A8CEFA"}
          >
            <Flex gap={"12px"} alignItems={"flex-start"}>
              <Flex
                alignItems={"center"}
                justifyContent={"center"}
                flexShrink={0}
                width={"32px"}
                height={"32px"}
                borderRadius={"8px"}
                backgroundColor={"#1272EF"}
                color={"#FFFFFF"}
              >
                <CustomIcon src={"package"} width={18} height={18} />
              </Flex>
              <Flex flexDirection={"column"} gap={"6px"}>
                <Text textStyle={"body-md-bold"} color={"#22221F"}>
                  {t("path_pick_guide:free_trial.finish_title")}
                </Text>
                <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                  {t("path_pick_guide:free_trial.description")}
                </Text>
                <Text textStyle={"caption-medium"} color={"#6F6F6C"}>
                  {t("path_pick_guide:free_trial.notice")}
                </Text>
              </Flex>
            </Flex>
          </PathPickStartGuideCard>
        )}

        <Flex gap={"8px"} justifyContent={"flex-start"}>
          <AppButton variant={"normal"} onClick={onPrev}>
            {t("path_pick_guide:nav.prev_step")}
          </AppButton>
          {isTrialEligible ? (
            <AppButton
              variant={"primary"}
              onClick={() => setIsTrialModalOpen(true)}
            >
              {t("path_pick_guide:free_trial.cta")}
            </AppButton>
          ) : (
            <AppButton variant={"primary"} onClick={goToSearchProducts}>
              {t("path_pick_guide:finish.search_cta")}
            </AppButton>
          )}
        </Flex>
      </Flex>

      <PathPickStartGuideFreeTrialModal
        isOpen={isTrialModalOpen}
        onClose={() => setIsTrialModalOpen(false)}
        onConfirm={() => {
          startTrial();
          setIsTrialModalOpen(false);
          goToSearchProducts();
        }}
      />
    </>
  );
}

export default PathPickStartGuideFinishSection;
